/**
 * Tight bounds of what a group actually draws, in the owning SVG's user units.
 *
 * `getBBox()` on an ancestor of a rotated group returns the axis-aligned box of an already
 * axis-aligned box, inflating it by up to root-2 per axis -- which makes anything that frames
 * itself from that box (fit-to-view, the Blueprint View page viewBox) shrink the design.
 * Measuring the leaves gives the real drawn extent instead.
 *
 * Only worth the extra reads when something inside IS rotated; plain `getBBox()` is exact otherwise.
 *
 * @param group group whose rendered contents should be measured
 * @returns bounds with x/y at the minimum corner, or null if the group cannot be measured
 */
export const getRenderedContentBounds = (group: SVGGraphicsElement | null): { x: number; y: number; width: number; height: number } | null => {
	const screenCtm = group?.ownerSVGElement?.getScreenCTM?.();
	if (!group || !screenCtm) return null;

	const toUser = screenCtm.inverse();
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

	group.querySelectorAll('path,rect,circle,ellipse,line,polygon,polyline,text,image,use').forEach((node) => {
		const rect = (node as SVGGraphicsElement).getBoundingClientRect();
		if (rect.width === 0 && rect.height === 0) return;

		// the owning SVG is never itself rotated, so a screen rect maps to an axis-aligned user rect
		const topLeft = new DOMPoint(rect.left, rect.top).matrixTransform(toUser);
		const bottomRight = new DOMPoint(rect.right, rect.bottom).matrixTransform(toUser);

		minX = Math.min(minX, topLeft.x, bottomRight.x);
		minY = Math.min(minY, topLeft.y, bottomRight.y);
		maxX = Math.max(maxX, topLeft.x, bottomRight.x);
		maxY = Math.max(maxY, topLeft.y, bottomRight.y);
	});

	if (!Number.isFinite(minX) || maxX - minX <= 0 || maxY - minY <= 0) return null;

	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};
